import { ArrowRight, Boxes, Database, FolderKanban, Plus, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { navigateToDashboard, navigateToWorkspaceProject } from '../App';
import AppHeader from '../components/workspace/AppHeader';
import MembersPanel from '../components/workspace/MembersPanel';
import NameDialog from '../components/workspace/NameDialog';
import { useWorkspaceStore } from '../store/useWorkspaceStore';

export default function OrganizationPage({ orgId }: { orgId: string }) {
  const org = useWorkspaceStore((s) => s.organizations.find((o) => o.id === orgId));
  const projects = useWorkspaceStore((s) => s.projects);
  const createProject = useWorkspaceStore((s) => s.createProject);
  const removeProject = useWorkspaceStore((s) => s.removeProject);
  const can = useWorkspaceStore((s) => s.can);
  const [showNew, setShowNew] = useState(false);
  const [tab, setTab] = useState<'projects' | 'members'>('projects');

  if (!org) {
    return (
      <div className="page-bg flex flex-col items-center justify-center gap-3 dark:text-gray-200">
        <p>Organization not found.</p>
        <button onClick={navigateToDashboard} className="btn-primary">
          Back to organizations
        </button>
      </div>
    );
  }

  const orgProjects = projects.filter((p) => p.orgId === orgId);
  const canManageProjects = can(orgId, 'projects.manage');

  return (
    <div className="page-bg">
      <AppHeader crumbs={[{ label: org.name }]} />
      <main className="mx-auto max-w-6xl px-6 pb-16 pt-12">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight dark:text-white">{org.name}</h1>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Projects in this organization. Each one can hold ER and architecture diagrams.</p>
          </div>
          {tab === 'projects' && canManageProjects && orgProjects.length > 0 && (
            <button onClick={() => setShowNew(true)} className="btn-primary">
              <Plus className="h-4 w-4" /> New project
            </button>
          )}
        </div>

        <div className="mb-8 flex gap-1 border-b border-gray-200 dark:border-white/10">
          {(
            [
              ['projects', 'Projects', FolderKanban],
              ['members', 'Members', Users],
            ] as const
          ).map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition ${
                tab === key
                  ? 'border-sky-500 text-sky-700 dark:text-sky-300'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {tab === 'members' ? (
          <MembersPanel org={org} />
        ) : orgProjects.length === 0 ? (
          <div className="surface px-8 py-16 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-400 text-white shadow-lg shadow-violet-500/30">
              <FolderKanban className="h-8 w-8" />
            </div>
            <h2 className="text-xl font-semibold dark:text-white">No projects yet</h2>
            <p className="mx-auto mb-8 mt-2 max-w-md text-gray-500 dark:text-gray-400">
              {canManageProjects
                ? 'Create a project for a product or system, then add database and architecture diagrams to it.'
                : 'Ask an owner or admin to create a project in this organization.'}
            </p>
            {canManageProjects && (
              <button onClick={() => setShowNew(true)} className="btn-primary px-5 py-2.5">
                <Plus className="h-4 w-4" /> Create project
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {orgProjects.map((p) => (
              <div key={p.id} onClick={() => navigateToWorkspaceProject(orgId, p.id)} className="surface-hover group relative p-5">
                {canManageProjects && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!confirm(`Delete project "${p.name}" and all its diagrams? This cannot be undone.`)) return;
                      try {
                        await removeProject(p.id);
                      } catch (err) {
                        alert(err instanceof Error ? err.message : 'Could not delete the project');
                      }
                    }}
                    className="absolute right-3 top-3 rounded-lg p-1.5 text-gray-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                    title="Delete project"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 ring-1 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30">
                  <FolderKanban className="h-5 w-5" />
                </div>
                <div className="truncate text-base font-semibold dark:text-gray-100">{p.name}</div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
                  <span className="flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
                    <Database className="h-3 w-3" /> {p.erIds.length} ER
                  </span>
                  <span className="flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
                    <Boxes className="h-3 w-3" /> {p.archIds.length} architecture
                  </span>
                </div>
                <ArrowRight className="absolute bottom-5 right-5 h-4 w-4 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-sky-500" />
              </div>
            ))}
          </div>
        )}
      </main>

      {showNew && canManageProjects && (
        <NameDialog
          title="New project"
          description={`Created inside ${org.name}.`}
          placeholder="e.g. Payments Platform"
          onCancel={() => setShowNew(false)}
          onConfirm={async (name) => {
            const p = await createProject(orgId, name);
            setShowNew(false);
            navigateToWorkspaceProject(orgId, p.id);
          }}
        />
      )}
    </div>
  );
}
